import { useAuth } from '@/context/Auth';
import { useLog } from '@/features/shared/Hooks/useLog';
import {
  SSO_CONTACT_REGISTRATION_PATH,
  SSO_INVITED_REGISTRATION_PATH,
  SSO_OLD_PORTAL_URL,
  SSO_ROOT,
} from '@/utils/constants';
import {
  isInvitedRegistrationPathname,
  isPortalPathname,
  isPortalRegistrationPathname,
} from '@/utils/portal';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { PortalRegistrationStatuses } from '@/context/Contact/useUserRegistrationStatus';

/**
 * Hook for handling user redirections between different portal sections
 * @returns Object containing redirect functions for different portal sections
 */
export const useUserRedirection = (): {
  redirectToInvitedRegistration: () => Promise<void>;
  redirectToContactRegistration: () => Promise<void>;
  redirectToPortal: () => Promise<void>;
  handleRegistrationDirection: ({
    portalRegistrationStatus,
    betaParticipantStatus,
  }: {
    portalRegistrationStatus: string | null | undefined;
    betaParticipantStatus: string | boolean | null | undefined;
  }) => Promise<void>;
  redirectToOldPortal: () => Promise<void>;
} => {
  const history = useHistory();
  const { killToken } = useAuth();
  const Log = useLog({ context: 'useUserRedirection' });

  /**
   * Generic handler for executing redirects
   * @param redirectFn Function to execute the redirect
   * @returns Promise that resolves after redirect is complete
   */
  const handleRedirect = useCallback(async (redirectFn: () => void): Promise<void> => {
    redirectFn();
    return Promise.resolve();
  }, []);

  /**
   * Redirects user to legacy portal after killing current session
   * @returns Promise that resolves after redirect is complete
   */
  const redirectToOldPortal = useCallback(async (): Promise<void> => {
    if (!isPortalPathname(window.location.pathname)) {
      return Promise.resolve();
    }

    Log('Killing existing session before redirect');
    killToken();

    return handleRedirect(() => {
      Log(`Redirecting to old portal with: ${SSO_OLD_PORTAL_URL}`);
      window.location.href = SSO_OLD_PORTAL_URL;
    });
  }, [Log, killToken, handleRedirect]);

  /**
   * Redirects user to contact registration page if not already there
   * @returns Promise that resolves after redirect is complete
   */
  const redirectToContactRegistration = useCallback(async (): Promise<void> => {
    if (
      !isPortalPathname(window.location.pathname) ||
      isPortalRegistrationPathname(window.location.pathname)
    ) {
      return Promise.resolve();
    }

    return handleRedirect(() => {
      Log(`Redirecting to contact registration with: ${SSO_CONTACT_REGISTRATION_PATH}`);
      history.replace(SSO_CONTACT_REGISTRATION_PATH);
    });
  }, [Log, history, handleRedirect]);

  /**
   * Redirects user to invited registration page if not already there
   * @returns Promise that resolves after redirect is complete
   */
  const redirectToInvitedRegistration = useCallback(async (): Promise<void> => {
    if (
      !isPortalPathname(window.location.pathname) ||
      isInvitedRegistrationPathname(window.location.pathname)
    ) {
      return Promise.resolve();
    }

    return handleRedirect(() => {
      Log(`Redirecting to invited registration with: ${SSO_INVITED_REGISTRATION_PATH}`);
      history.replace(SSO_INVITED_REGISTRATION_PATH);
    });
  }, [Log, history, handleRedirect]);

  const redirectToPortal = useCallback(
    () =>
      handleRedirect(() => {
        Log('Redirecting to portal');
        history.replace(SSO_ROOT);
      }),
    [Log, handleRedirect, history],
  );

  /**
   * Handles redirecting users to the appropriate registration page based on their portal and beta status
   *
   * @param portalRegistrationStatus - The user's current portal registration status
   * @param betaParticipantStatus - The user's beta participant status
   * @returns Promise that resolves after any redirect is complete
   */
  const handleRegistrationDirection = useCallback(
    async ({
      portalRegistrationStatus,
      betaParticipantStatus,
    }: {
      portalRegistrationStatus: string | null | undefined;
      betaParticipantStatus: string | boolean | null | undefined;
    }): Promise<void> => {
      switch (portalRegistrationStatus) {
        case PortalRegistrationStatuses.PENDING:
          return redirectToContactRegistration();
        case PortalRegistrationStatuses.INVITED:
          return redirectToInvitedRegistration();
        case PortalRegistrationStatuses.APPROVED:
          if (!betaParticipantStatus) {
            return redirectToOldPortal();
          }
          if (isInvitedRegistrationPathname(window.location.pathname)) {
            return redirectToPortal();
          }
          break;
        default:
          return;
      }
    },
    [
      redirectToContactRegistration,
      redirectToInvitedRegistration,
      redirectToOldPortal,
      redirectToPortal,
    ],
  );

  return {
    redirectToOldPortal,
    redirectToContactRegistration,
    redirectToInvitedRegistration,
    redirectToPortal,
    handleRegistrationDirection,
  };
};
